import { useTranslation } from 'react-i18next';
import {
    Box,
    Flex,
    IconButton,
    Tooltip,
    Divider,
    HStack,
} from '@chakra-ui/react';
import {
    FiMousePointer,
    FiMove,
    FiZoomIn,
    FiEdit3,
    FiCircle,
    FiSquare,
    FiBox,
    FiGrid,
    FiLayers,
    FiRotateCcw,
    FiRotateCw,
    FiAlignLeft,
    FiCopy,
    FiScissors,
    FiMinimize2,
} from 'react-icons/fi';
import { useMemo } from 'react';
import { useAppStore, ToolType } from '../../state';
import { useCommandManager } from '../../state/commandStore';
import { executeDraftCommand } from '../../core/dynamicInput/dynamicInputEngine';
import { toolsForWorkspace, type WorkspaceId } from '../../core/toolCatalog';
import { useLocation } from 'react-router-dom';
import { KeyboardShortcutsHelp } from '../help';

/**
 * Icon + command-id presentation map per catalog tool id. The catalog
 * (core/toolCatalog) is the single source of truth for WHICH tools exist
 * and WHICH workspaces surface them; the toolbar only maps them to icons
 * and their registrar command id for shortcut tooltips. Tools absent from
 * this map are intentionally not button-mapped yet.
 */
const TOOL_PRESENTATION: Record<string, { icon: React.ElementType; cmdId: string }> = {
    select: { icon: FiMousePointer, cmdId: 'toolSelect' },
    orbit: { icon: FiMove, cmdId: 'toolOrbit' },
    pan: { icon: FiMove, cmdId: 'toolPan' },
    zoom: { icon: FiZoomIn, cmdId: 'toolZoom' },
    line: { icon: FiEdit3, cmdId: 'toolLine' },
    circle: { icon: FiCircle, cmdId: 'toolCircle' },
    rectangle: { icon: FiSquare, cmdId: 'toolRectangle' },
    arc: { icon: FiEdit3, cmdId: 'toolArc' },
    polygon: { icon: FiGrid, cmdId: 'toolPolygon' },
    ellipse: { icon: FiCircle, cmdId: 'toolEllipse' },
    text: { icon: FiEdit3, cmdId: 'activateText' },
    dimLinear: { icon: FiEdit3, cmdId: 'toolLinearDimension' },
    curve: { icon: FiEdit3, cmdId: 'toolCurve' },
    box: { icon: FiBox, cmdId: 'toolBox' },
    sphere: { icon: FiCircle, cmdId: 'toolSphere' },
    wall: { icon: FiSquare, cmdId: 'activateWall' },
    door: { icon: FiSquare, cmdId: 'activateDoor' },
    window: { icon: FiSquare, cmdId: 'activateWindow' },
    floor: { icon: FiSquare, cmdId: 'activateFloor' },
    column: { icon: FiSquare, cmdId: 'activateColumn' },
    extrude: { icon: FiBox, cmdId: 'toolExtrude' },
    loft: { icon: FiBox, cmdId: 'toolLoft' },
    sweep: { icon: FiBox, cmdId: 'toolSweep' },
    revolve: { icon: FiBox, cmdId: 'toolRevolve' },
    MOVE: { icon: FiCopy, cmdId: 'move' },
    COPY: { icon: FiCopy, cmdId: 'copy' },
    ROTATE: { icon: FiRotateCw, cmdId: 'rotate' },
    SCALE: { icon: FiMinimize2, cmdId: 'scale' },
    MIRROR_PICK: { icon: FiCopy, cmdId: 'mirrorPick' },
    ALIGN: { icon: FiAlignLeft, cmdId: 'align' },
    TRIM: { icon: FiScissors, cmdId: 'trim' },
    OFFSET: { icon: FiAlignLeft, cmdId: 'offset' },
    DELETE: { icon: FiScissors, cmdId: 'delete' },
    EXTEND: { icon: FiMove, cmdId: 'extend' },
    FILLET: { icon: FiRotateCw, cmdId: 'fillet' },
    CHAMFER: { icon: FiMinimize2, cmdId: 'chamfer' },
    ARRAY: { icon: FiGrid, cmdId: 'array' },
};

/**
 * Main Toolbar Component
 * 
 * Replicates the toolbar experience from:
 * - BIM: Ribbon-style tabs (simplified in MVP)
 * - 2D: Quick access toolbar
 * - 3D: Top toolbar with tool icons
 *
 * Phase A4: the visible tool set is FILTERED FROM the shared tool catalog
 * for the current workspace — never hardcoded — so a tool registered for
 * workbench X shows up in X's toolbar automatically.
 */
export function Toolbar() {
    const { t } = useTranslation();
    const activeTool = useAppStore((s) => s.activeTool);
    const setActiveTool = useAppStore((s) => s.setActiveTool);
    const undo = useAppStore((s) => s.undo);
    const redo = useAppStore((s) => s.redo);
    const toggleSidebar = useAppStore((s) => s.toggleSidebar);
    const togglePropertiesPanel = useAppStore((s) => s.togglePropertiesPanel);
    const gridVisible = useAppStore((s) => s.view.gridVisible);
    const updateView = useAppStore((s) => s.updateView);

    const location = useLocation();
    const commands = useCommandManager((s) => s.commands);

    // Current (possibly user-assigned) shortcut string for a command id.
    const sc = (cmdId: string) => commands[cmdId]?.shortcut || '';
    // Tooltip label with the shortcut in parentheses, e.g. "Wall (WA)".
    const shortcutLabel = (label: string, cmdId: string) => {
        const s = sc(cmdId);
        return s ? `${label} (${s})` : label;
    };

    // Get current mode from URL
    const currentMode = location.pathname.replace('/', '') || 'bim';

    // A4: build the visible tool buttons FROM the shared catalog, filtered
    // for the current workspace. Presentation (icon + registrar command id
    // for the shortcut tooltip) is layered on top; catalog entries without
    // a presentation mapping are not button-mapped yet. Memoized on the
    // workspace: the toolbar re-renders on every command-store change (the
    // shortcut tooltips read `commands`) and this used to rebuild per render.
    const workspace: WorkspaceId = (['bim', '2d', '3d', 'sketch'] as const).includes(currentMode as WorkspaceId)
        ? (currentMode as WorkspaceId)
        : 'bim';
    const tools = useMemo(() => toolsForWorkspace(workspace)
        .filter((entry) => TOOL_PRESENTATION[entry.tool])
        .map((entry) => ({
            tool: entry.tool,
            icon: TOOL_PRESENTATION[entry.tool].icon,
            label: entry.label ?? String(entry.tool),
            group: entry.group ?? 'draw',
            cmdId: TOOL_PRESENTATION[entry.tool].cmdId,
        })), [workspace]);

    const handleToolClick = (tool: ToolType) => {
        // Tools with no pointer scope/hook in this shell: type the command
        // through the shared draft executor so the canonical runner or the
        // correct class tool actually acts (the sphere primitive runs an
        // interaction; nav ids report the real mouse model; DELETE in the
        // drafting shells erases CAD entities).
        const t = String(tool);
        if (t === 'sphere') { executeDraftCommand('SPHERE'); return; }
        if (t === 'zoom' || t === 'pan' || t === 'orbit') { executeDraftCommand(t.toUpperCase()); return; }
        if (t === 'DELETE' && currentMode !== 'bim') { executeDraftCommand('ERASE'); return; }
        // CAD modify commands whose uppercase ids only have BIM class tools
        // (TRIM/FILLET/EXTEND/CHAMFER/ARRAY/HATCH): in the drafting shells,
        // route through the command line so the CAD-entity runners act.
        if (currentMode !== 'bim' && ['TRIM', 'EXTEND', 'FILLET', 'CHAMFER', 'ARRAY', 'HATCH'].includes(t)) {
            executeDraftCommand(t);
            return;
        }
        setActiveTool(tool);
    };

    return (
        <Box
            h="40px"
            bg="surface.200"
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
            px={2}
        >
            <Flex h="100%" align="center" gap={1}>
                {/* Undo/Redo */}
                <Tooltip label={shortcutLabel('Undo', 'undo')}>
                    <IconButton
                        icon={<FiRotateCcw />}
                        variant="ghost"
                        size="sm"
                        aria-label={t('ui.toolbar.undo')}
                        onClick={undo}
                    />
                </Tooltip>
                <Tooltip label={shortcutLabel('Redo', 'redo')}>
                    <IconButton
                        icon={<FiRotateCw />}
                        variant="ghost"
                        size="sm"
                        aria-label={t('ui.toolbar.redo')}
                        onClick={redo}
                    />
                </Tooltip>

                <Divider orientation="vertical" h="24px" />

                {/* Tool Groups */}
                {['nav', 'draw', 'modify', '3d', 'bim'].map((group, groupIdx) => (
                    <HStack key={group} spacing={0}>
                        {groupIdx > 0 && <Divider orientation="vertical" h="24px" mx={1} />}
                        {tools
                            .filter((t) => t.group === group)
                            .map((t) => (
                                <Tooltip key={t.tool} label={shortcutLabel(t.label, t.cmdId)}>
                                    <IconButton
                                        icon={<t.icon />}
                                        variant="ghost"
                                        size="sm"
                                        aria-label={t.label}
                                        isActive={activeTool === t.tool}
                                        onClick={() => handleToolClick(t.tool)}
                                        bg={activeTool === t.tool ? 'brand.500' : 'transparent'}
                                        _hover={{ bg: activeTool === t.tool ? 'brand.600' : 'whiteAlpha.100' }}
                                    />
                                </Tooltip>
                            ))}
                    </HStack>
                ))}

                {/* Spacer */}
                <Box flex="1" />

                {/* View Controls */}
                <Tooltip label={shortcutLabel('Toggle Grid', 'toggleGrid')}>
                    <IconButton
                        icon={<FiGrid />}
                        variant="ghost"
                        size="sm"
                        aria-label={t('ui.toolbar.toggleGrid')}
                        isActive={gridVisible}
                        onClick={() => updateView({ gridVisible: !gridVisible })}
                    />
                </Tooltip>
                <Tooltip label={shortcutLabel('Toggle Layers Panel', 'toggleLayersPanel')}>
                    <IconButton
                        icon={<FiLayers />}
                        variant="ghost"
                        size="sm"
                        aria-label={t('ui.toolbar.toggleLayers')}
                        onClick={toggleSidebar}
                    />
                </Tooltip>
                <Tooltip label={shortcutLabel('Toggle Properties', 'showProperties')}>
                    <IconButton
                        icon={<FiSquare />}
                        variant="ghost"
                        size="sm"
                        aria-label={t('ui.toolbar.toggleProperties')}
                        onClick={togglePropertiesPanel}
                    />
                </Tooltip>

                <Divider orientation="vertical" h="24px" />

                {/* Shortcuts Help — Collaboration / Plugins triggers moved up
                    to the suite topbar's right cluster in every workbench. */}
                <KeyboardShortcutsHelp />
            </Flex>
        </Box>
    );
}
